import React from "react";

import { IconDefinition, IconProp } from "@fortawesome/fontawesome-svg-core";
import { Link } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { Button } from "src/base-components/Button";
import { Icon } from "src/design-system/Icon";
import { Tooltip, TooltipProps } from "src/design-system/Tooltip";

const VARIANTS = {
  default: "bg-indigo-100",
  error: "bg-red-100",
} as const;

const SIZES = {
  default: "py-16",
  compact: "py-4",
} as const;

/**
 * Button styling is intentionally locked to `variant="secondary"` and the
 * default size — these are the only states the Figma EmptyState design
 * specifies. `size`, `variant`, and arbitrary `ReactNode` action slots are
 * deliberately omitted so consumers cannot drift from the design.
 */
export type ActionButtonProps = {
  label: string;
  icon?: IconDefinition;
  isDisabled?: boolean;
  isLoading?: boolean;
  dataLoc?: string;
  tooltip?: Pick<TooltipProps, "title" | "body" | "placement" | "disabled">;
} & (
  | {
      onClick: React.MouseEventHandler<HTMLButtonElement>;
      linkTo?: never;
      openInNewTab?: never;
    }
  | {
      linkTo: string;
      openInNewTab?: boolean;
      onClick?: never;
    }
);

export type EmptyProps = {
  dataLoc?: string;
  icon: IconProp;
  headline: string;
  description?: React.ReactNode | undefined;
  /** @deprecated Use `actionButton` instead */
  action?: React.ReactNode;
  variant?: keyof typeof VARIANTS;
  size?: keyof typeof SIZES;
  actionButton?:
    | ActionButtonProps
    | [ActionButtonProps, ActionButtonProps]
    | false;
};

const ActionButton: React.FC<ActionButtonProps & { fullWidth?: boolean }> = ({
  label,
  icon,
  isDisabled,
  isLoading,
  dataLoc,
  tooltip,
  fullWidth,
  ...action
}) => {
  const button = (
    <Button
      dataLoc={dataLoc}
      disabled={isDisabled}
      fullWidth={fullWidth}
      iconLeft={icon}
      loading={isLoading}
      variant="secondary"
      onClick={action.onClick}
    >
      {label}
    </Button>
  );

  const buttonWithTooltip = tooltip ? (
    <Tooltip {...tooltip} asChild>
      {button}
    </Tooltip>
  ) : (
    button
  );

  if (!action.linkTo) {
    return buttonWithTooltip;
  }

  if (action.openInNewTab) {
    return (
      <a href={action.linkTo} rel="noopener noreferrer" target="_blank">
        {buttonWithTooltip}
      </a>
    );
  }

  return <Link to={action.linkTo}>{buttonWithTooltip}</Link>;
};

export const EmptyState: React.FC<EmptyProps> = ({
  dataLoc,
  icon,
  headline,
  description,
  action,
  actionButton,
  variant = "default",
  size = "default",
}) => {
  const buttons = actionButton
    ? Array.isArray(actionButton)
      ? actionButton
      : [actionButton]
    : [];
  const hasMultiple = buttons.length > 1;

  return (
    <div
      className={twJoin(
        "flex h-full flex-col items-center justify-center text-center",
        SIZES[size],
      )}
      data-loc={dataLoc}
    >
      <div
        aria-hidden="true"
        className={twJoin(
          "mb-4 inline-block rounded-lg p-3",
          VARIANTS[variant],
        )}
      >
        <Icon
          icon={icon}
          size="md"
          variant={variant === "error" ? "error" : "active"}
        />
      </div>
      <h2
        className="mb-2 inline-block max-w-60 font-inter-semibold-13px text-gray-800"
        data-record="true"
      >
        {headline}
      </h2>
      <p
        className={twJoin(
          "inline-block min-h-0 max-w-60 overflow-hidden break-words font-inter-normal-12px text-gray-500",
          (!!action || buttons.length > 0) && "mb-6",
        )}
      >
        {description}
      </p>
      {buttons.length > 0 && (
        <div
          className={twJoin(
            "flex gap-2",
            hasMultiple && "flex-col items-center",
          )}
        >
          {buttons.map((btn, i) => {
            const button = (
              <ActionButton
                {...btn}
                dataLoc={
                  btn.dataLoc ??
                  (hasMultiple ? `${dataLoc}-action-${i}` : `${dataLoc}-action`)
                }
                fullWidth={hasMultiple}
              />
            );
            return (
              <React.Fragment key={i}>
                {hasMultiple && i > 0 && (
                  <p className="font-inter-medium-12px text-gray-500">or</p>
                )}
                {hasMultiple ? <div className="w-60">{button}</div> : button}
              </React.Fragment>
            );
          })}
        </div>
      )}
      {action}
    </div>
  );
};
